/**
 * Impersonation Banner Component
 * WHY: Shows persistent amber banner when superadmin is "viewing as" another user.
 * Uses sessionStorage (client-side only — never touches auth or Supabase auth session).
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export interface ImpersonationData {
  userId: string;
  userEmail: string;
  userTier: string;
  originalSuperadminId: string;
}

export function getImpersonationData(): ImpersonationData | null {
  try {
    const userId = sessionStorage.getItem("impersonating_user_id");
    const userEmail = sessionStorage.getItem("impersonating_user_email");
    const userTier = sessionStorage.getItem("impersonating_user_tier");
    const originalSuperadminId = sessionStorage.getItem("original_superadmin_id");
    if (userId && userEmail && userTier && originalSuperadminId) {
      return { userId, userEmail, userTier, originalSuperadminId };
    }
  } catch {}
  return null;
}

export function clearImpersonation() {
  sessionStorage.removeItem("impersonating_user_id");
  sessionStorage.removeItem("impersonating_user_email");
  sessionStorage.removeItem("impersonating_user_tier");
  sessionStorage.removeItem("original_superadmin_id");
}

// HIDDEN, NOT DELETED. CLAUDE.md LOCKED 2026-05-13: "Impersonations
// disconnect. Surface unused. Hide from nav. No code deletion, just
// disconnection." impersonation_logs does not exist in the database, so this
// banner could never reflect a real session. Delete this constant and the
// guard below to bring it back.
const SURFACE_HIDDEN = true;

export function ImpersonationBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState<ImpersonationData | null>(null);

  useEffect(() => {
    setData(getImpersonationData());
  }, []);

  // After the hooks, never before — rules-of-hooks is an error in this repo and
  // an early return above useEffect is the exact crash class that white-screened
  // the dashboard on 2026-07-24.
  if (SURFACE_HIDDEN) return null;

  if (!data) return null;

  const tierLabel = data.userTier.charAt(0).toUpperCase() + data.userTier.slice(1);

  const handleReturn = async () => {
    // Update ended_at on the impersonation log
    const logId = sessionStorage.getItem("impersonation_log_id");
    if (logId) {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { error: phantomErr } = await (supabase as any)
          .from("impersonation_logs")
          .update({ ended_at: new Date().toISOString() })
          .eq("id", logId);
        // This relation does not exist, so this write cannot have succeeded.
        if (phantomErr) console.error('[unavailable] ImpersonationBanner.tsx:', phantomErr.message);
      } catch {}
      sessionStorage.removeItem("impersonation_log_id");
    }
    clearImpersonation();
    navigate("/admin");
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] bg-amber-500 text-black py-2 px-4 flex items-center justify-between shadow-lg">
      <div className="flex items-center gap-3">
        <Eye className="h-4 w-4 shrink-0" />
        <span className="font-semibold text-sm">
          👁 Viewing as{" "}
          <span className="font-bold">{data.userEmail}</span>
        </span>
        <Badge className="bg-black/20 text-black border-black/20 text-xs">
          {tierLabel} tier
        </Badge>
        <span className="text-xs text-black/60 hidden md:inline">
          — This banner is only visible to you. Data shown is this user's real data.
        </span>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={handleReturn}
        className="bg-white/30 border-black/30 text-black hover:bg-white/50 font-semibold shrink-0"
      >
        <ArrowLeft className="h-3 w-3 mr-1" />
        ↩ Return to Superadmin
      </Button>
    </div>
  );
}
